import React from "react";
import { ASSETS } from "@/lib/assets";
import { ChevronLeft, Swords, Loader, Footprints, Crosshair, Zap, Sparkles } from "lucide-react";
import { Stat } from "./shared";
import { DecisionGroup } from "./DecisionGroup";
import { PredictedOutcome } from "./PredictedOutcome";

export const PersonalSetup = ({
  navigate, opts, state,
  approach, setApproach, target, setTarget, tactic, setTactic,
  personalForce, setPersonalForce, customTactic, setCustomTactic,
  personalBrief, loadingPersonalBrief,
  suggesting, suggestPlan, planRationale,
  submitPersonal, resolving,
}) => (
  <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-10 lg:py-14">
    <button onClick={() => navigate("/command")} className="btn-ghost mb-6 flex items-center gap-1" data-testid="battle-back-button">
      <ChevronLeft className="w-3 h-3" /> COMMAND
    </button>

    <p className="tac-label fade-up">PERSONAL DUEL // {opts.preview.region_name}</p>
    <h1 className="font-serif-h text-5xl lg:text-7xl uppercase text-bone leading-none mt-3 fade-up"
        data-testid="battle-name-heading">
      {opts.preview.battle_name}
    </h1>
    <p className="text-sm text-muted-h mt-3 italic max-w-2xl fade-up delay-1" data-testid="battle-intel">
      "{opts.preview.intel}"
    </p>

    {/* Enriched battle field intel */}
    <div className="mt-6 narrative-panel max-w-3xl fade-up delay-1" data-testid="battle-field-intel">
      <p className="tac-label mb-3">ON THE FIELD</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Stat label="HUMANS" value={(opts.preview.humans || 0).toLocaleString()} />
        <Stat label="WEAPONS" value={`${Math.round((opts.preview.weapons || 0) * 100)}%`} />
        <Stat label="FORTIFIED" value={`${Math.round((opts.preview.fortification || 0) * 100)}%`} />
        <Stat label="CONVERT %" value={`${Math.round((opts.preview.expected_convert_rate || 0) * 100)}%`} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs font-mono">
        <div className="border-l-2 border-[#7FBA9D]/40 pl-2" data-testid="comp-civilians">
          <p className="text-[0.65rem] text-muted-h tracking-wider">CIVILIANS</p>
          <p className="text-bone mt-0.5">{(opts.preview.civilians_count || 0).toLocaleString()}</p>
          <p className="text-[0.6rem] text-muted-h">75% turn</p>
        </div>
        <div className="border-l-2 border-bone/40 pl-2" data-testid="comp-soldiers">
          <p className="text-[0.65rem] text-muted-h tracking-wider">ARMED SOLDIERS</p>
          <p className="text-bone mt-0.5">{(opts.preview.soldiers_count || 0).toLocaleString()}</p>
          <p className="text-[0.6rem] text-muted-h">50% turn</p>
        </div>
        <div className="border-l-2 border-blood/60 pl-2" data-testid="comp-officers">
          <p className="text-[0.65rem] text-muted-h tracking-wider">OFFICERS</p>
          <p className="text-bone mt-0.5">{(opts.preview.officers_count || 0).toLocaleString()}</p>
          <p className="text-[0.6rem] text-muted-h">25% turn</p>
        </div>
      </div>
    </div>

    <div className="mt-8 flex items-start gap-4 fade-up delay-1">
      <button
        onClick={suggestPlan}
        disabled={suggesting}
        className="btn-bone flex items-center gap-2"
        data-testid="suggest-plan-button"
      >
        {suggesting
          ? <><Loader className="w-3 h-3 animate-spin" /> READING THE BONES…</>
          : <><Sparkles className="w-3 h-3" /> SUGGEST PLAN</>}
      </button>
      {planRationale && (
        <div className="border-l-2 border-blood pl-3 py-1 max-w-2xl"
             data-testid="plan-rationale">
          <p className="tac-label">QUARTERMASTER</p>
          <p className="text-sm text-[var(--text-main)] italic mt-1 leading-relaxed">
            "{planRationale}"
          </p>
        </div>
      )}
    </div>

    <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
      <DecisionGroup
        icon={Footprints}
        title="APPROACH"
        options={opts.approaches}
        selected={approach}
        setSelected={setApproach}
        testidPrefix="approach"
      />
      <DecisionGroup
        icon={Crosshair}
        title="TARGET"
        options={opts.targets}
        selected={target}
        setSelected={setTarget}
        testidPrefix="target"
      />
      <DecisionGroup
        icon={Zap}
        title="TACTIC"
        options={opts.tactics}
        selected={tactic}
        setSelected={(t) => {
          setTactic(t);
          if (t?.key === "send_personal_army") {
            setPersonalForce(Math.max(1, Math.floor(state.personal_horde / 2)));
          }
          if (t?.key !== "other") setCustomTactic("");
        }}
        testidPrefix="tactic"
        hordeSize={state.personal_horde}
        sliderValue={personalForce}
        setSliderValue={setPersonalForce}
        customValue={customTactic}
        setCustomValue={setCustomTactic}
      />
    </div>

    {(approach && target && tactic) && (
      <div className="mt-10 narrative-panel relative fade-up"
           data-testid="personal-briefing-panel">
        <div className="absolute inset-0 opacity-15 pointer-events-none">
          <img src={ASSETS.horde} alt="" className="w-full h-full object-cover mix-blend-overlay" />
        </div>
        <div className="relative">
          <p className="tac-label mb-3">PRE-FIGHT INTEL</p>
          {loadingPersonalBrief && !personalBrief ? (
            <p className="text-sm text-muted-h flex items-center gap-2"
               data-testid="personal-briefing-loading">
              <Loader className="w-3 h-3 animate-spin" /> The Squad Leader is drawing the path in the dirt&hellip;
            </p>
          ) : personalBrief ? (
            <p className="text-base leading-relaxed text-[var(--text-main)] italic whitespace-pre-line"
               data-testid="personal-briefing-text">
              {personalBrief}
            </p>
          ) : (
            <p className="text-sm text-muted-h" data-testid="personal-briefing-empty">
              No intel.
            </p>
          )}
        </div>
      </div>
    )}

    {/* Predicted outcome — appears once all 3 are locked */}
    {approach && target && tactic && (
      <PredictedOutcome
        approach={approach}
        target={target}
        tactic={tactic}
        preview={opts.preview}
        horde={state.personal_horde}
        personalForce={tactic.key === "send_personal_army" ? personalForce : 0}
      />
    )}

    <button onClick={submitPersonal}
            disabled={resolving || !approach || !target || !tactic
                      || (tactic.key === "other" && customTactic.trim().length < 5)}
            className="btn-blood mt-6" data-testid="engage-personal-button">
      {resolving ? <><Loader className="w-3 h-3 animate-spin" /> ...</> : <><Swords className="w-3 h-3" /> STRIKE</>}
    </button>
  </div>
);
